import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage, injectIntl } from 'react-intl';
import InputElement from 'react-input-mask';
import cx from 'classnames';
import InputHint from 'components/Credit24/Core/Controls/InputHint';
import {
  getAndroidVersion, isChrome, keyErrorsFieldRequired, removeNonDigitCharacters,
} from 'utils/helpers';

const propTypes = {
  input: PropTypes.object,
  meta: PropTypes.object,
  type: PropTypes.string,
  title: PropTypes.any,
  disabled: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isTxtCenter: PropTypes.bool,
  isMaskedInput: PropTypes.bool,
  isErrorVisible: PropTypes.bool,
  forcedErrorOnSubmit: PropTypes.bool,
  isTextButton: PropTypes.bool,
  mask: PropTypes.string,
  maskChar: PropTypes.string,
  hintConfig: PropTypes.shape({
    titleKey: PropTypes.string.isRequired,
    hasLetters: PropTypes.bool,
    hasNumbers: PropTypes.bool,
    hasCapitalLetters: PropTypes.bool,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
  }),
  explanationTextKey: PropTypes.string,
  prefix: PropTypes.string,
  useDigitsGrouping: PropTypes.bool,
  defaultPasswordVisibility: PropTypes.oneOf(['visible', 'hidden']),
  autoComplete: PropTypes.string,
  intl: PropTypes.object.isRequired,
};

const defaultProps = {
  defaultPasswordVisibility: 'hidden',
  autoComplete: 'on',
};

const isOldAndroidVersion = getAndroidVersion() && parseInt(getAndroidVersion(), 10) < 5;

class InputField extends Component {
  constructor(props) {
    super(props);
    let fieldType = props.type || 'text';
    if (props.type === 'password' && props.defaultPasswordVisibility === 'visible') {
      fieldType = 'text';
    }
    this.state = {
      type: fieldType,
    };
    this.getValueLength = this.getValueLength.bind(this);
    this.getWasPrefilledByChrome = this.getWasPrefilledByChrome.bind(this);
    this.togglePasswordInputType = this.togglePasswordInputType.bind(this);
    this.handleCursor = this.handleCursor.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.type !== nextProps.type) {
      this.setState({ type: nextProps.type });
    }
  }

  getWasPrefilledByChrome() {
    return isChrome() && !!document.querySelector(`.input-txt__field[name=${this.props.input.name}]:-webkit-autofill`);
  }

  getValueLength() {
    const { value } = this.props.input;
    if (this.state.type === 'number' && value >= 0) {
      return value.toString().length;
    }
    return `${value}`.length;
  }

  togglePasswordInputType() {
    this.setState({
      // eslint-disable-next-line react/no-access-state-in-setstate -- node upgrade
      type: this.state.type === 'password' ? 'text' : 'password',
    });
  }

  // eslint-disable-next-line react/sort-comp -- node upgrade
  handleCursor({ target }) {
    const { selectionStart, selectionEnd, value } = target;
    // Don't do anything if user selects a range
    if (selectionStart === selectionEnd) {
      const firstMaskIndex = value.indexOf(this.props.maskChar);
      if (firstMaskIndex >= 0 && firstMaskIndex < selectionStart) {
        // Shift the cursor to the left until first written text.
        target.setSelectionRange(firstMaskIndex, firstMaskIndex);
      }
    }
  }

  render() {
    const {
      input,
      meta: {
        touched, error, active, submitFailed,
      },
      title,
      type,
      disabled,
      mask,
      maskChar,
      isFullWidth,
      isTxtCenter,
      isMaskedInput,
      isErrorVisible,
      forcedErrorOnSubmit,
      isTextButton,
      hintConfig,
      explanationTextKey,
      prefix,
      useDigitsGrouping,
      autoComplete,
      intl,
      // eslint-disable-next-line react/prop-types
      errorValues,
    } = this.props;

    const isFocused = active && !disabled;
    const isErrorTriggered = touched && error
            && error !== keyErrorsFieldRequired && isErrorVisible;
    const isInErrorState = touched && (error || (forcedErrorOnSubmit && submitFailed));

    const inputClasses = cx({
      'input-txt': true,
      'input-txt--width-full': isFullWidth,
      'input-txt--with-hint': hintConfig || isErrorTriggered,
      'input-txt--state-error': isInErrorState,
      'input-txt--state-disabled': disabled,
      'input-txt--state-active': this.getValueLength() || (prefix && isFocused) || (!touched && this.getWasPrefilledByChrome()),
      'input-txt--state-focused': isFocused,
      'input-txt--prefix': prefix,
    });

    const fieldClasses = cx({
      'input-txt__field': true,
      'txt-center': isTxtCenter,
    });

    const toggleIconClasses = cx({
      'input-txt__toggle-icon': true,
      'input-txt__toggle-icon--type-text': isTextButton,
      'input-txt__toggle-icon--shown': type === 'password' && this.state.type === 'text',
    });

    let inputValue = input.value;
    if (useDigitsGrouping && inputValue && inputValue !== 0) {
      inputValue = intl.formatNumber(
        removeNonDigitCharacters(inputValue),
        { useGrouping: true, maximumFractionDigits: 0 }
      );
    }

    let Input = (
      <input
        {...input}
        className={fieldClasses}
        type={this.state.type}
        autoComplete={type === 'password' ? 'off' : autoComplete}
        value={inputValue}
      />
    );

    if (isMaskedInput && !isOldAndroidVersion) {
      Input = (
        <InputElement
          {...input}
          className={fieldClasses}
          mask={mask}
          maskChar={maskChar}
          onClick={this.handleCursor}
          autoComplete={autoComplete}
        />
      );
    }

    let key = error;
    if (hintConfig && !isErrorTriggered) {
      key = hintConfig.titleKey;
    }

    const inputHint = hintConfig
      ? (
        <InputHint
          {...hintConfig}
          value={inputValue}
          titleKey={key}
          isBlock={!isFocused}
        />
      )
      : null;

    const inputError = !hintConfig && isErrorTriggered
      ? (
        <span className={isFocused ? 'input-txt__error' : 'input-message input-message--error'}>
          {
              errorValues
                ? <FormattedHTMLMessage id={key} values={errorValues} />
                : <FormattedMessage id={key} />
          }

        </span>
      )
      : null;

    return (
      <>
        <label className={inputClasses} data-prefix={prefix}>
          {title
                        && <span className="input-txt__title">{title}</span>}
          {Input}
          {isFocused && inputHint}
          {isFocused && inputError}
          {type === 'password'
                        && (
                        <button
                          className={toggleIconClasses}
                          onClick={this.togglePasswordInputType}
                          type="button"
                          tabIndex="-1"
                        >
                          {isTextButton
                                    && (
                                    <FormattedMessage
                                      id={this.state.type !== 'text' ? 'password.show' : 'password.hide'}
                                    />
                                    )}
                        </button>
                        )}
        </label>
        {!isFocused && isInErrorState && inputHint}
        {!isFocused && isInErrorState && inputError}
        {explanationTextKey
                    && (
                    <span className="input-explanation">
                      <FormattedMessage
                        id={explanationTextKey}
                      />
                    </span>
                    )}
      </>
    );
  }
}

InputField.propTypes = propTypes;
InputField.defaultProps = defaultProps;

export default injectIntl(InputField);
